/**
 * RFC 4180 CSV reader (KTD2: hand-rolled, no new dependency). Handles the
 * subset real address-book exports actually use: quoted fields containing
 * commas, newlines and doubled quotes, a UTF-8 BOM, and CRLF line endings.
 *
 * v1's `utils/linkedinCsv.ts` split on `,` and mangled every quoted company
 * name; nothing from that parser is reused.
 */

const BOM = "﻿";

export const stripBom = (text: string): string =>
	text.startsWith(BOM) ? text.slice(1) : text;

export interface CsvResult {
	readonly rows: string[][];
	/** 1-based index of the row that opened a quote the file never closed. */
	readonly unterminatedRow?: number | undefined;
}

/**
 * Reads `text` into rows of fields, stopping once `maxRows` rows exist so a
 * hostile file cannot be walked to its end before the caller refuses it.
 *
 * ponytail: character-at-a-time scan, ~20 M iterations at the 20 MB ceiling.
 * Swap in a chunked scan over unquoted runs if import ever feels slow.
 */
export const readCsv = (
	text: string,
	maxRows = Number.POSITIVE_INFINITY,
): CsvResult => {
	const source = stripBom(text);
	const rows: string[][] = [];
	let row: string[] = [];
	let field = "";
	let started = false;
	let quoted = false;
	let quoteRow: number | undefined;

	const endField = () => {
		row.push(field);
		field = "";
		started = true;
	};
	const endRow = () => {
		endField();
		rows.push(row);
		row = [];
		started = false;
	};

	for (let i = 0; i < source.length && rows.length < maxRows; i += 1) {
		const character = source.charAt(i);
		if (quoted) {
			if (character !== '"') {
				field += character;
			} else if (source.charAt(i + 1) === '"') {
				field += '"';
				i += 1;
			} else {
				quoted = false;
				quoteRow = undefined;
			}
			continue;
		}
		if (character === '"' && field === "") {
			quoted = true;
			quoteRow = rows.length + 1;
			continue;
		}
		if (character === ",") {
			endField();
			continue;
		}
		if (character === "\r") continue;
		if (character === "\n") {
			endRow();
			continue;
		}
		field += character;
	}

	if (rows.length < maxRows && (started || field !== "")) endRow();

	return { rows, unterminatedRow: quoteRow };
};

/** Rows only, for callers that do not care about quoting failures. */
export const parseCsv = (text: string, maxRows?: number): string[][] =>
	readCsv(text, maxRows).rows;

/**
 * Reads a field by header name (R2: never by fixed position — the vendor
 * formats reorder and add columns between exports).
 */
export const headerReader =
	(header: readonly string[]) =>
	(row: readonly string[], name: string): string => {
		const index = header.indexOf(name);
		return index === -1 ? "" : (row[index] ?? "").trim();
	};
